import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { todayInWorkspaceTz, type CreateTaskInput } from "@/hooks/useWorkspaceTasks";
import { useWorkspaceProjects, useProjectColumns } from "@/hooks/useWorkspaceProjects";
import { TASK_PRIORITY_OPTIONS, type TaskPriority } from "@/lib/task-priority";
import { validateSubtaskList } from "@/lib/task-date-validation";
import { TaskTagsInput } from "./TaskTagsInput";
import { TaskLeadSelect } from "./TaskLeadSelect";
import { TaskDatePicker } from "./TaskDatePicker";
import { TaskTimePicker } from "./TaskTimePicker";
import {
  TaskDraftSubtaskSection,
  type DraftSubtask,
} from "./TaskDraftSubtaskSection";

interface WorkspaceMemberOption {
  id: string;
  display_name: string | null;
  user_id: string;
}

interface TaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  memberId: string;
  isAdmin: boolean;
  onCreate: (input: CreateTaskInput) => Promise<void>;
  defaultProjectId?: string | null;
  defaultColumnId?: string | null;
}

export function TaskDialog({
  open,
  onOpenChange,
  workspaceId,
  memberId,
  isAdmin,
  onCreate,
  defaultProjectId = null,
  defaultColumnId = null,
}: TaskDialogProps) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [assigneeId, setAssigneeId] = useState(memberId);
  const [priority, setPriority] = useState<TaskPriority>("medium");
  const [tags, setTags] = useState<string[]>([]);
  const [leadId, setLeadId] = useState<string | null>(null);
  const [noDueDate, setNoDueDate] = useState(false);
  const [dueDate, setDueDate] = useState(todayInWorkspaceTz());
  const [dueTime, setDueTime] = useState("");
  const [members, setMembers] = useState<WorkspaceMemberOption[]>([]);
  const [subtasks, setSubtasks] = useState<DraftSubtask[]>([]);
  const [projectId, setProjectId] = useState<string | null>(defaultProjectId);
  const [columnId, setColumnId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();
  const { projects } = useWorkspaceProjects(workspaceId);
  const { columns, loading: columnsLoading } = useProjectColumns(projectId);

  useEffect(() => {
    if (!open) return;
    setTitle("");
    setDescription("");
    setAssigneeId(memberId);
    setPriority("medium");
    setTags([]);
    setLeadId(null);
    setNoDueDate(false);
    setDueDate(todayInWorkspaceTz());
    setDueTime("");
    setSubtasks([]);
    setProjectId(defaultProjectId);
    setColumnId(defaultColumnId);
    if (!isAdmin) return;
    void (async () => {
      const { data } = await supabase
        .from("workspace_members")
        .select("id, display_name, user_id")
        .eq("workspace_id", workspaceId)
        .order("display_name", { ascending: true });
      setMembers((data ?? []) as WorkspaceMemberOption[]);
    })();
  }, [open, workspaceId, memberId, isAdmin, defaultProjectId, defaultColumnId]);

  useEffect(() => {
    if (!open || !projectId || columnsLoading) return;
    if (columns.length === 0) {
      setColumnId(null);
      return;
    }
    setColumnId((current) => {
      if (current && columns.some((c) => c.id === current)) return current;
      if (defaultColumnId && columns.some((c) => c.id === defaultColumnId)) {
        return defaultColumnId;
      }
      return columns[0]?.id ?? null;
    });
  }, [open, projectId, columns, columnsLoading, defaultColumnId]);

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast({ title: "Informe um título", variant: "destructive" });
      return;
    }
    if (!noDueDate) {
      const today = todayInWorkspaceTz();
      if (dueDate < today) {
        toast({
          title: "Data inválida",
          description: "Não é possível criar tarefa com data no passado.",
          variant: "destructive",
        });
        return;
      }
    }
    const parentDueDate = noDueDate ? null : dueDate;
    const subtaskError = validateSubtaskList(parentDueDate, subtasks);
    if (subtaskError) {
      toast({
        title: "Datas das subtarefas inválidas",
        description: subtaskError.message,
        variant: "destructive",
      });
      return;
    }
    try {
      setSubmitting(true);
      const due_date = noDueDate ? null : dueDate;
      const dueAt =
        !noDueDate && dueTime ? new Date(`${dueDate}T${dueTime}:00`).toISOString() : null;
      await onCreate({
        title: title.trim(),
        description: description.trim() || null,
        assigned_member_id: isAdmin ? assigneeId : memberId,
        due_date,
        due_at: dueAt,
        priority,
        lead_id: leadId,
        tags,
        project_id: projectId,
        project_column_id: projectId && columnId ? columnId : null,
        subtasks: subtasks.map((s) => ({
          title: s.title,
          description: s.description,
          assignee_member_ids: s.assignee_member_ids,
          due_date: s.due_date,
          due_at:
            s.due_date && s.due_time
              ? new Date(`${s.due_date}T${s.due_time}:00`).toISOString()
              : null,
        })),
      });
      onOpenChange(false);
      toast({ title: "Tarefa criada" });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({
        title: "Erro ao criar tarefa",
        description: message,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-0 p-0 sm:max-w-2xl">
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle>Nova tarefa</DialogTitle>
        </DialogHeader>

        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
          <div className="space-y-2">
            <Label htmlFor="task-title">Título</Label>
            <Input
              id="task-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex.: Ligar para o lead João"
              className="h-11 text-base font-medium"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="task-desc">Descrição (opcional)</Label>
            <Textarea
              id="task-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="resize-none"
              placeholder="Adicione detalhes, contexto ou um checklist do que precisa ser feito."
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Prioridade</Label>
              <Select value={priority} onValueChange={(v) => setPriority(v as TaskPriority)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TASK_PRIORITY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {isAdmin && (
              <div className="space-y-2">
                <Label>Responsável</Label>
                <Select value={assigneeId} onValueChange={setAssigneeId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecionar membro" />
                  </SelectTrigger>
                  <SelectContent>
                    {members.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.display_name || "(sem nome)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Projeto (opcional)</Label>
              <Select
                value={projectId ?? "none"}
                onValueChange={(v) => {
                  setProjectId(v === "none" ? null : v);
                  setColumnId(null);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sem projeto (Inbox)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sem projeto (Inbox)</SelectItem>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {projectId && columns.length > 0 && (
              <div className="space-y-2">
                <Label>Coluna</Label>
                <Select value={columnId ?? ""} onValueChange={setColumnId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecionar coluna" />
                  </SelectTrigger>
                  <SelectContent>
                    {columns.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <TaskLeadSelect workspaceId={workspaceId} value={leadId} onChange={setLeadId} />
            <div className="space-y-2">
              <Label>Tags (opcional)</Label>
              <TaskTagsInput tags={tags} onChange={setTags} />
            </div>
          </div>

          <div className="space-y-3 rounded-lg border bg-muted/30 p-4">
            <div className="flex items-center gap-2">
              <Checkbox
                id="task-no-due-date"
                checked={noDueDate}
                onCheckedChange={(v) => setNoDueDate(v === true)}
              />
              <Label htmlFor="task-no-due-date" className="cursor-pointer font-normal">
                Sem data definida
              </Label>
            </div>
            {!noDueDate && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="task-date">Data</Label>
                  <TaskDatePicker
                    id="task-date"
                    value={dueDate}
                    onChange={setDueDate}
                    minDate={todayInWorkspaceTz()}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="task-time">Horário (opcional)</Label>
                  <TaskTimePicker
                    id="task-time"
                    value={dueTime}
                    onChange={setDueTime}
                    clearable
                  />
                </div>
              </div>
            )}
          </div>

          <div className="border-t pt-5">
            <TaskDraftSubtaskSection
              drafts={subtasks}
              onChange={setSubtasks}
              memberId={memberId}
              isAdmin={isAdmin}
              members={members}
              minDate={todayInWorkspaceTz()}
              parentDueDate={noDueDate ? null : dueDate}
            />
          </div>
        </div>

        <DialogFooter className="border-t px-6 py-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Criar tarefa
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
